import { useId, useMemo } from "react";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { canManageScheduleMasterData } from "@/features/auth/permissions";
import { useLatestRun, useRun } from "../api/use-runs";
import { useSchedule } from "../api/use-schedules";
import { computeSectionRoster } from "../lib/compute-section-roster";
import { formatEntityLabel } from "../lib/format-entity-label";
import type { Assignment, Section } from "../types";
import type { ReassignHandler } from "./reassign-handler";
import { SidePanel } from "./side-panel";

interface SectionRosterPanelProps {
  scheduleId: string;
  section: Section;
  onClose: () => void;
  onReassign: ReassignHandler;
  /** See {@link SidePanel.skipFocusRestore}. */
  skipFocusRestore?: boolean;
}

/**
 * Right-side drawer triggered from a Sections-tab row. Same primitive +
 * roster computation as `EventDetailPanel`, but the header is keyed on a
 * `Section` (no event summary; sections have no per-section validation banner
 * — the Validation Issues tab handles that).
 */
export function SectionRosterPanel({
  scheduleId,
  section,
  onClose,
  onReassign,
  skipFocusRestore = false,
}: SectionRosterPanelProps) {
  const titleId = useId();
  const { user } = useAuth();
  const canReassign = canManageScheduleMasterData(user?.role);

  const scheduleQuery = useSchedule(scheduleId);
  const timeZone = scheduleQuery.data?.timeZone ?? "UTC";

  const latestRun = useLatestRun(scheduleId);
  const runQuery = useRun(scheduleId, latestRun.data?.id ?? "");
  const run = runQuery.data;

  const roster = useMemo<Assignment[]>(
    () =>
      computeSectionRoster(
        run,
        section.acceptedNormalizedCourseIds,
        section.sectionLabel,
      ),
    [run, section.acceptedNormalizedCourseIds, section.sectionLabel],
  );

  const showRosterPlaceholder = !latestRun.data || runQuery.isLoading;

  const sectionLabel = section.sectionLabel ?? "—";
  const timeLabel = formatSectionTime(section, timeZone);
  const assignedCount = roster.length;
  const capacity = section.capacity;
  const overCapacity = capacity != null && assignedCount > capacity;
  const underCapacity = capacity != null && assignedCount < capacity;

  const assignedBadgeClass = overCapacity
    ? "bg-red-500/20 text-red-300"
    : underCapacity
      ? "bg-amber-500/20 text-amber-300"
      : "bg-emerald-500/20 text-emerald-300";

  return (
    <SidePanel
      onClose={onClose}
      ariaLabelledBy={titleId}
      testId="section-roster-panel"
      skipFocusRestore={skipFocusRestore}
    >
      <div className="flex items-start justify-between gap-3 px-5 py-[18px] border-b border-border-subtle">
        <div className="min-w-0">
          <div
            id={titleId}
            data-testid="section-roster-header"
            className="font-mono text-[13px] font-semibold text-blue-300"
          >
            {formatEntityLabel(
              section.courseName,
              (section.acceptedCourseIds.length > 0
                ? section.acceptedCourseIds
                : [section.courseId]
              ).join(" / "),
            )}{" "}
            · Section {sectionLabel}
          </div>
          <p
            data-testid="section-roster-subhead"
            className="mt-1 text-[12px] text-text-muted"
          >
            {section.teacherEmail ?? "—"} · {section.roomName ?? "—"} ·{" "}
            {timeLabel}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          data-testid="section-roster-close"
          className="text-text-muted hover:text-white text-[20px] leading-none cursor-pointer"
        >
          ×
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-[18px]">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[12px] font-semibold uppercase tracking-wider text-text-muted">
            Roster
          </span>
          <span
            data-testid="section-roster-count-badge"
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${assignedBadgeClass}`}
          >
            {assignedCount}
            {capacity != null ? `/${capacity}` : ""}
          </span>
        </div>

        {showRosterPlaceholder ? (
          <div
            data-testid="section-roster-no-run"
            className="bg-page border border-border-subtle rounded-md p-4 text-[13px] text-text-muted"
          >
            No run available — run the scheduler to see rosters.
          </div>
        ) : roster.length === 0 ? (
          <div
            data-testid="section-roster-empty"
            className="bg-page border border-border-subtle rounded-md p-4 text-[13px] text-text-muted"
          >
            No students assigned to this section.
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {roster.map((assignment) => (
              <SectionRosterRow
                key={assignment.id}
                assignment={assignment}
                canReassign={canReassign}
                onReassign={() =>
                  onReassign(
                    assignment.studentIdentifier,
                    section.courseId,
                    section.sectionLabel,
                    assignment.studentName,
                    section.courseName,
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

interface SectionRosterRowProps {
  assignment: Assignment;
  canReassign: boolean;
  onReassign: () => void;
}

function SectionRosterRow({
  assignment,
  canReassign,
  onReassign,
}: SectionRosterRowProps) {
  const initials = computeInitials(assignment.studentIdentifier);
  return (
    <div
      data-testid={`section-roster-row-${assignment.id}`}
      className="flex items-center justify-between bg-page border border-border-subtle rounded-md px-3 py-2"
    >
      <div className="flex items-center gap-2.5">
        <div
          aria-hidden="true"
          className="w-6 h-6 rounded-full bg-surface-2 flex items-center justify-center text-[10px] font-semibold text-text-soft"
        >
          {initials}
        </div>
        <span className="text-[13px] text-text-soft">
          {formatEntityLabel(
            assignment.studentName,
            assignment.studentIdentifier,
          )}
        </span>
      </div>
      {canReassign && (
        <button
          type="button"
          onClick={onReassign}
          data-testid={`section-roster-reassign-${assignment.id}`}
          className="text-[12px] text-blue-400 hover:text-blue-300 cursor-pointer"
        >
          Reassign
        </button>
      )}
    </div>
  );
}

function computeInitials(identifier: string): string {
  const cleaned = identifier.trim();
  if (cleaned.length === 0) return "??";
  const parts = cleaned.split(/[\s_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

function formatSectionTime(section: Section, timeZone: string): string {
  if (!section.earliestStartUtc || !section.latestEndUtc) return "—";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    });
    const start = fmt.format(new Date(section.earliestStartUtc));
    const end = fmt.format(new Date(section.latestEndUtc));
    return `${start} – ${end}`;
  } catch {
    return "—";
  }
}
